import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  MultiCountryAddSurveyDataMapper,
  MultiCountryFailedLocalesMapper,
  MultiCountrySameCountryExclusionPayloadMapper,
} from '../domain/multi-country-traditional-view-data-mapper';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  toArray,
} from 'rxjs/operators';
import { Observable, Subscription, forkJoin, from, merge, of } from 'rxjs';
import { AuthService } from '../../../../../shared/services/auth/auth.service';
import { MultiCountryService } from '../services/multi-country.service';
import {
  PatchSurveyInterface,
  SurveyNoTargetingResponse,
} from '../../../../../shared/interfaces/survey.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { notifyMessage } from '../../../../../shared/constants/notify-message';
import {
  McPreDraftSurvey,
  McSpvSurveyNoTargetingResponse,
  McSpvSurveyRequest,
  McSurveyRequest,
} from '../interface/multi-country.interface';
import { MultiCountrySvpAddSurveyMapper } from '../domain/multi-country-single-view-data-mapper';
import { BuyerApiService } from '../../../../../shared/services/buyer-api/survey.service';
import { MultiCountryDraftService } from '../../../../services/multi-country-draft/multi-country-draft.service';

@Component({
  selector: 'ps-ui-multi-country-pre-draft',
  templateUrl: './multi-country-pre-draft.component.html',
  styleUrls: ['./multi-country-pre-draft.component.css'],
})
export class MultiCountryPreDraftComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() multiCountryPreDraftData: McPreDraftSurvey[] = [];
  @Output() multiCountryPreDraftDataUpdate = new EventEmitter<
    McPreDraftSurvey[]
  >();

  private _selectionChangeCommand$ = new EventEmitter<Array<string>>();
  private _bulkSelectionCommand$ = new EventEmitter<
    'select-all' | 'clear-all'
  >();
  private _createdSurveyCommand$ = new EventEmitter<
    {
      response: SurveyNoTargetingResponse;
      payload: McSurveyRequest;
    }[]
  >();

  private _subscriptions = new Subscription();
  private _multipleSurveysCreationCommand$ = new EventEmitter();
  private _mcSvpCreationCommand$ = new EventEmitter();

  public selectedLocales: string[] = [];
  public isMultiCountryEnabled: boolean = false;
  public isMultiCountrySingleViewEnabled: boolean = false;

  constructor(
    private _auth: AuthService,
    private _multiCountryDraftService: MultiCountryDraftService,
    private _multiCountryService: MultiCountryService,
    private _router: Router,
    private _toaster: ToasterService,
    private _buyerApiService: BuyerApiService
  ) {}

  public ngOnInit(): void {
    this._initializeBuyerConfigs();
    this._handleMCPreDraftDataChanges();
    this._handleSurveysCreation();
    this._handleCreatedSurveyNavigation();
    this._handleMcSVPCreation();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.multiCountryPreDraftData.currentValue) {
      this.selectedLocales = this._selectedLocales();
    }
  }

  private _selectedLocales(): Array<string> {
    return this.multiCountryPreDraftData
      .filter((item) => !!item.checked)
      .map((item) => item.survey_localization);
  }

  private _initializeBuyerConfigs(): void {
    this.isMultiCountryEnabled = !!this._auth.buyerConfig?.enableMultiCountry;
    this.isMultiCountrySingleViewEnabled =
      !!this._auth.buyerConfig?.enableMultiCountrySingleProjectView;
  }

  private _handleMCPreDraftDataChanges(): void {
    const singleSelectionStream$ = this._selectionChangeCommand$.pipe(
      switchMap((selectedLocales) => this._mapDataOnSelection(selectedLocales))
    );

    const multiSelectionStream$ = this._bulkSelectionCommand$.pipe(
      switchMap((state) => this._mapDataOnBulkSelection(state))
    );

    this._subscriptions.add(
      merge(singleSelectionStream$, multiSelectionStream$).subscribe((data) => {
        this.multiCountryPreDraftData = data;
        this.selectedLocales = this._selectedLocales();
        this.multiCountryPreDraftDataUpdate.emit(data);
      })
    );
  }

  public onLocaleSelection(selectedLocales: Array<string>) {
    this._selectionChangeCommand$.next(selectedLocales);
  }

  public onToggleAllSelections(state: 'select-all' | 'clear-all') {
    this._bulkSelectionCommand$.next(state);
  }

  private _mapDataOnSelection(
    selectedLocales: Array<string>
  ): Observable<McPreDraftSurvey[]> {
    return from(this.multiCountryPreDraftData).pipe(
      map((item) => {
        item.checked = !!selectedLocales.includes(item.survey_localization);
        return item;
      }),
      toArray()
    );
  }

  private _mapDataOnBulkSelection(
    state: 'select-all' | 'clear-all'
  ): Observable<McPreDraftSurvey[]> {
    return from(this.multiCountryPreDraftData).pipe(
      map((item) => {
        item.checked = state === 'select-all';
        return item;
      }),
      toArray()
    );
  }

  public onSingleSurveyCreation(): void {
    this._mcSvpCreationCommand$.next();
  }

  private _handleMcSVPCreation(): void {
    this._subscriptions.add(
      this._mcSvpCreationCommand$
        .pipe(
          filter(() => this.isMultiCountrySingleViewEnabled),
          switchMap(() => this._filterOutUncheckedLocales()),
          filter((mcPreDraftSurveys) => !!mcPreDraftSurveys.length),
          map((mcPreDraftSurveys) =>
            new MultiCountrySvpAddSurveyMapper(mcPreDraftSurveys).transform()
          ),
          switchMap((payload) => this._createMcSpvSurvey(payload))
        )
        .subscribe((parentSurvey) => {
          this._multiCountryDraftService.setMultiCountryPreDraftData([]);
          this._toaster.success(
            notifyMessage.successMessage.MULTICOUNTRY_SINGLE_VIEW
              .CREATED_SUCCESSFULLY
          );
          this._router.navigateByUrl(
            `/surveys/${parentSurvey.ps_survey_id}/edit`
          );
        })
    );
  }

  private _createMcSpvSurvey(
    payload: McSpvSurveyRequest
  ): Observable<McSpvSurveyNoTargetingResponse> {
    return this._multiCountryService
      .addMCSpvSurvey(payload)
      .pipe(
        filter(
          (response): response is McSpvSurveyNoTargetingResponse => !!response
        )
      );
  }

  public onMultipleSurveysCreation(): void {
    this._multipleSurveysCreationCommand$.emit();
  }

  private _handleSurveysCreation(): void {
    this._subscriptions.add(
      this._multipleSurveysCreationCommand$
        .pipe(
          filter(() => this.isMultiCountryEnabled),
          switchMap(() => this._filterOutUncheckedLocales()),
          map((mcPreDraftSurveys) =>
            new MultiCountryAddSurveyDataMapper(
              mcPreDraftSurveys,
              this._auth.isLoggedInAsServiceBuyer()
            ).transform()
          ),
          switchMap((payloads) => this._createMcSurveys(payloads)),
          map(
            (surveys) =>
              <
                [
                  {
                    response: SurveyNoTargetingResponse;
                    payload: McSurveyRequest;
                  }[],
                  { error: unknown; payload: McSurveyRequest }[]
                ]
              >[
                surveys.filter((survey) => survey.hasOwnProperty('response')),
                surveys.filter((survey) => survey.hasOwnProperty('error')),
              ]
          ),
          filter(([, failedSurveys = []]) => {
            if (failedSurveys.length) {
              const failedLocales = new MultiCountryFailedLocalesMapper(
                failedSurveys
              ).transform();
              this._toaster.error(
                `${
                  notifyMessage.errorMessage.MULTICOUNTRY_DRAFT.REJECTED_SURVEYS
                } ${failedLocales.join(', ')}`
              );
              return false;
            }

            this._toaster.success(
              notifyMessage.successMessage.MULTICOUNTRY_DRAFT.SAVED_SUCCESS
            );
            return true;
          }),
          switchMap(([createdSurveyData]) =>
            this._patchSurveyExclusionsPostSurveyCreation(createdSurveyData)
          )
        )
        .subscribe((createdSurveyData = []) => {
          this._multiCountryDraftService.setMultiCountryPreDraftData([]);
          this._createdSurveyCommand$.next(createdSurveyData);
        })
    );
  }

  private _handleCreatedSurveyNavigation(): void {
    this._subscriptions.add(
      this._createdSurveyCommand$
        .pipe(
          filter((data) => !!data.length),
          map((data) => {
            const parentSurvey = data.find((item) => item.payload.mc_parent);
            // If parent survey found, redirect to parent; Otherwise redirect to the first available survey
            return parentSurvey
              ? parentSurvey.response.ps_survey_id
              : data[0].response.ps_survey_id;
          })
        )
        .subscribe((surveyId) => {
          this._router.navigateByUrl(`/surveys/${surveyId}/edit`);
        })
    );
  }

  private _filterOutUncheckedLocales(): Observable<McPreDraftSurvey[]> {
    return from(this.multiCountryPreDraftData).pipe(
      filter((mcPreDraftSurveys) => mcPreDraftSurveys.checked),
      map((mcPreDraftSurveys) => mcPreDraftSurveys),
      toArray()
    );
  }

  private _createMcSurveys(
    payloads: McSurveyRequest[]
  ): Observable<
    (
      | { error: unknown; payload: McSurveyRequest }
      | { response: SurveyNoTargetingResponse; payload: McSurveyRequest }
    )[]
  > {
    const addSurveys$ = payloads.map((payload) =>
      this._multiCountryService.addSurvey(payload)
    );
    return forkJoin(addSurveys$);
  }

  private _patchSurveyExclusionsPostSurveyCreation(
    data: { payload: McSurveyRequest; response: SurveyNoTargetingResponse }[]
  ) {
    const allResponses = data.map((s) => s.response);

    return from(data).pipe(
      map(
        ({ response: currentResponse }) =>
          <[number, PatchSurveyInterface[]]>[
            currentResponse.ps_survey_id,
            new MultiCountrySameCountryExclusionPayloadMapper(
              allResponses,
              currentResponse
            ).transform(),
          ]
      ),
      mergeMap(([surveyId, payload]) => {
        if (!(payload[0].value as number[]).length) {
          return of([]);
        }
        return this._buyerApiService
          .patchSurvey(surveyId, payload)
          .pipe(catchError(() => of({})));
      }),
      map(() => data)
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
